// faqData.js - The questions people actually ask, grouped. Used by the FAQ page,
// the home page preview, and the FAQPage structured data, so an answer only ever
// has to be written once.
//
// Rule for answers: say the real thing, in the fewest words, including the part
// that is inconvenient for us. Vague answers are what people expect from this
// industry and they are the reason nobody trusts it.

export const FAQ_GROUPS = [
  {
    title: "Getting started",
    items: [
      {
        question: "What does the kit cost?",
        answer: "Nothing. The mailer is free, the postage out to you is paid, and the return label inside is prepaid and insured. There is no fee for asking, and no fee if you look at a real offer and decide not to sell. The one exception is in the terms and we would rather you heard it here: if what arrives turns out to be counterfeit, plated, or base metal, you pay the return postage. Genuine metal always comes back free.",
      },
      {
        question: "How long does the whole thing take?",
        answer: "Most people have money in their account within a week of asking for a kit. The kit takes two to three days to reach you, the return trip is FedEx 2Day, and we usually finish the evaluation within a day of the package landing.",
      },
      {
        question: "Do I have to sell once I send my items?",
        answer: "No. You see the full itemised offer before you agree to anything. If you decline, we pack everything exactly as it arrived and ship it back insured at our cost. There is no restocking fee and no return shipping charge. Two things do not come back on those terms: anything we valued at zero because it is not precious metal, and anything under a law enforcement hold. Both are in the terms.",
      },
      {
        question: "Is there a minimum?",
        answer: "There is no hard minimum, but a kit costs us real money to send, so we look at what you describe on the form first. If it is very small we will tell you plainly rather than send a mailer that is not worth your time.",
      },
    ],
  },
  {
    title: "Shipping and safety",
    items: [
      {
        question: "Is my package insured?",
        answer: "Yes, in both directions, to $1,000, and there is one thing you have to do for it to hold. Use our label, hand the package to a person at a staffed FedEx counter, and keep the receipt with the scan on it. Cover runs from that scan until the package reaches our counter, and the return trip is covered too, at our cost. A package dropped in an unstaffed box with no scan is the one case where a claim can fail. Sending more than $1,000 of metal is the one case where you have to tell us first.",
        link: { to: "/extra-coverage", label: "Sending more than $1,000?" },
      },
      {
        question: "What if the package is lost?",
        answer: "It is covered by the insurance on the label from the moment FedEx scans it. Tell us as soon as you suspect a problem, send us your drop-off receipt and the photos you took before sealing it, and we handle the claim. This is exactly why we ask you to hand it to a staffed counter and keep the receipt: without a scan there is nothing for the insurer to look at. Claims can take thirty business days or more, which is not fast, and we would rather say so.",
      },
      {
        question: "How much can I send in one kit?",
        answer: "Every shipment is covered to $1,000 automatically, with nothing to fill in, and that covers almost everything people send us. If what you are sending is worth more than that, ask us before you post it. We approve higher cover case by case, up to $25,000, and it only applies to a shipment we agreed to in advance. A request that arrives after your items are in the post cannot be backdated.",
        link: { to: "/extra-coverage", label: "Ask for higher cover" },
      },
      {
        question: "Do you really record the opening?",
        answer: "Every package, every time, from sealed envelope to the scale. The video appears on your tracking page alongside the itemised weights, so you can watch it before you answer the offer.",
      },
    ],
  },
  {
    title: "Pricing and payment",
    items: [
      {
        question: "How do you work out the offer?",
        answer: "We weigh each piece, deduct any stones or non-metal parts, and test the purity. That gives the actual precious metal content in grams. The offer is based on that content and the metal price on the day we quote you. Every one of those numbers is on your tracking page, so you can check the arithmetic.",
      },
      {
        question: "Why is the offer less than the melt value I calculated online?",
        answer: "A melt calculator shows what the pure metal in a piece is worth at spot. A buyer has to refine it, insure it, ship it, and take the price risk between buying and selling, so no legitimate buyer anywhere pays full spot. We show you the metal content and the offer next to each other so you can compare us to anybody else on the same numbers.",
      },
      {
        question: "How do I get paid?",
        answer: "Bank transfer, Zelle, Cash App, PayPal, or a mailed check, whichever you pick. Accept before 3pm Eastern on a business day and the transfer goes out the same day. Bank transfers usually land within one to two business days. If we still need to verify your identity, or the law requires us to report the transaction first, it takes longer, and we will tell you why rather than leave you waiting.",
      },
      {
        question: "How long is my offer good for?",
        answer: "Forty eight hours by default, because metal prices move and we are holding that price for you. Your tracking page shows the exact time it expires. Taking longer costs you nothing: reply to any of our messages and we requote at the current price, free, as many times as you need.",
      },
    ],
  },
  {
    title: "Your items",
    items: [
      {
        question: "What do you buy?",
        answer: "Gold, silver, platinum and palladium in almost any form: broken and single earrings, chains, rings, class rings, dental gold, coins, bars, sterling flatware and hollowware, and industrial or lab scrap. We buy pieces you would be embarrassed to take into a shop.",
      },
      {
        question: "What about the diamonds and stones?",
        answer: "We deduct stone weight so you are never paid metal rates for a stone, and we return the stones with your items unless you ask us to quote them separately. Small melee stones are usually not worth removing, and we will tell you when that is the case.",
      },
      {
        question: "Do I need to clean or sort anything?",
        answer: "No. Send it as it is. Sorting is our job, and mixed lots are completely normal. Do not polish anything, and do not try to remove stones yourself.",
      },
      {
        question: "What if I change my mind after I ship?",
        answer: "Tell us before you accept the offer and we send everything back, insured, at our cost. The only point of no return is the moment you press accept on your tracking page.",
      },
    ],
  },
  {
    title: "Identity and paperwork",
    items: [
      {
        question: "Why do you need my ID?",
        answer: "Precious metals dealers are required to verify the identity of the person they buy from and to keep a record that the seller owns the items. It protects you as much as us. Your ID images are encrypted and are never shared with anyone outside the business.",
      },
      {
        question: "Do I have to give bank details?",
        answer: "Only if you choose a bank transfer. Zelle needs a phone number or email, Cash App needs a tag, and a check needs a mailing address. Whatever you give us is encrypted, and account numbers are masked everywhere in our own system.",
      },
      {
        question: "Can I sell items I inherited?",
        answer: "Yes, that is most of what we buy. You confirm on the form that the items are yours to sell, which is the same declaration any shop would ask you to sign in person.",
      },
    ],
  },
];

// A flat list, for schema.org and for search.
export const ALL_FAQS = FAQ_GROUPS.flatMap((group) => group.items);

// The four that answer the objections people have before they ever fill the form.
export const HOME_FAQS = [
  ALL_FAQS.find((item) => item.question === "What does the kit cost?"),
  ALL_FAQS.find((item) => item.question === "Do I have to sell once I send my items?"),
  ALL_FAQS.find((item) => item.question === "How do you work out the offer?"),
  ALL_FAQS.find((item) => item.question === "How long does the whole thing take?"),
].filter(Boolean);
